
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { updatePassword } from "@/services/authService";
import { IconInput } from "./IconInput";
import { SubmitButton } from "./SubmitButton";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function UpdatePasswordForm() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Check if passwords match for real-time validation
  const passwordsMatch = password === confirmPassword || confirmPassword === "";
  
  // Check if the form is valid for submit button enablement
  const isFormValid = 
    password.trim() !== "" && 
    confirmPassword.trim() !== "" && 
    password === confirmPassword &&
    password.length >= 6;
  
  // Check if we have a valid hash token in the URL from Supabase
  useEffect(() => {
    // The Supabase Auth system will automatically handle the token
    // We just need to check that we're on this page because of a reset flow
    const hash = window.location.hash;
    const query = new URLSearchParams(window.location.search);
    
    // If we don't have a type=recovery in the hash or query params, the user might have 
    // navigated here directly which isn't valid for the reset flow
    const isResetFlow = 
      hash.includes('type=recovery') || 
      query.get('type') === 'recovery';
    
    if (!isResetFlow) {
      setHasValidToken(false);
    }
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem. Tente novamente.",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await updatePassword(password);
      toast({
        title: "Senha atualizada",
        description: "Sua senha foi alterada com sucesso.",
        variant: "default",
      });
      // Redirect after a short delay to allow the user to see the success message
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error: any) {
      console.error("Update password error:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar sua senha. Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasValidToken) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertDescription>
            Link de redefinição de senha inválido ou expirado. Por favor, solicite um novo link de redefinição.
          </AlertDescription>
        </Alert>
        <button 
          onClick={() => navigate('/reset-password')}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-teal rounded-md hover:bg-brand-teal/90"
        >
          Solicitar novo link
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold">Crie uma nova senha</h2>
        <p className="text-gray-600 mt-1 dark:text-gray-400">
          Digite e confirme sua nova senha abaixo.
        </p>
      </div>

      <div className="space-y-4">
        <IconInput
          id="password"
          label="Nova senha"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Digite sua nova senha"
          required
          disabled={isLoading}
          Icon={Lock}
          minLength={6}
        />
        
        <IconInput
          id="confirmPassword"
          label="Confirme a senha"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Digite novamente sua senha"
          required
          disabled={isLoading}
          Icon={Lock}
          minLength={6}
          error={!passwordsMatch ? "As senhas não coincidem" : undefined}
        />
      </div>

      <div className="mt-6 pt-2">
        <SubmitButton
          isLoading={isLoading}
          text="Atualizar senha"
          loadingText="Atualizando..."
          disabled={!isFormValid}
        />
      </div>
    </form>
  );
}
